import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function MyDogs({ playerId }) {
  const [dogs, setDogs] = useState([]);
  const [message, setMessage] = useState('');
  const [replies, setReplies] = useState({});
  const [breedFilter, setBreedFilter] = useState('');
  const [kennelFilter, setKennelFilter] = useState('');
  const [transferUsername, setTransferUsername] = useState({});
  const [collapsed, setCollapsed] = useState({
    approved: false,
    pending: false,
    denied: false,
    manage: false
  });

  useEffect(() => {
    if (playerId && playerId !== '') {
      fetch(`${API_BASE_URL}/my-dogs/${playerId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
          return res.json();
        })
        .then(data => setDogs(data))
        .catch(err => setMessage('Failed to load dogs: ' + err.message));
    } else {
      setDogs([]);
    }
  }, [playerId]);

  const sendReply = async (dogId) => {
    try {
      const text = replies[dogId] || '';
      if (!text) return;
      const res = await fetch(`${API_BASE_URL}/dog-message`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ dogId, senderId: playerId, senderRole: 'player', text })
      });
      if (!res.ok) throw new Error(`Reply failed: ${res.status}`);
      const data = await res.json();
      setMessage(data.message);
      setDogs(dogs.map(d => 
        d._id === dogId ? { ...d, messages: [...d.messages, { senderId: playerId, senderRole: 'player', text, timestamp: new Date(), unread: true }] } : d
      ));
      setReplies(prev => ({ ...prev, [dogId]: '' }));
    } catch (error) {
      setMessage('Reply failed: ' + error.message);
    }
  };

  const markMessageRead = async (dogId, msgIdx) => {
    try {
      const dog = dogs.find(d => d._id === dogId);
      const updatedDog = { ...dog, messages: dog.messages.map((m, i) => i === msgIdx ? { ...m, unread: false } : m) };
      const res = await fetch(`${API_BASE_URL}/dog/${dog.dogId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify(updatedDog)
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      setDogs(dogs.map(d => d._id === dogId ? updatedDog : d));
    } catch (error) {
      setMessage('Failed to mark as read: ' + error.message);
    }
  };

  const clearDeniedDog = async (dogId) => {
    const dog = dogs.find(d => d._id === dogId);
    try {
      const res = await fetch(`${API_BASE_URL}/dog/${dog.dogId}`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        }
      });
      if (!res.ok) throw new Error(`Clear failed: ${res.status}`);
      setDogs(dogs.filter(d => d._id !== dogId));
      setMessage('Dog cleared!');
    } catch (error) {
      setMessage('Clear failed: ' + error.message);
    }
  };

  const markDeceased = async (dogId) => {
    const dog = dogs.find(d => d.dogId === dogId);
    const confirmMessage = `Are you sure you want to mark "${dog.registeredName}" as deceased? This action cannot be undone, and the dog will be moved from your accepted dogs and placed on the In Memoriam page. Deceased dogs can no longer participate in gameplay.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      const res = await fetch(`${API_BASE_URL}/dog/${dogId}/decease`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ playerId })
      });
      if (!res.ok) throw new Error(await res.text());
      setDogs(dogs.filter(d => d.dogId !== dogId));
      setMessage('Dog marked as deceased');
    } catch (error) {
      setMessage('Failed to mark deceased: ' + error.message);
    }
  };

  const transferDog = async (dogId) => {
    const username = transferUsername[dogId];
    if (!username) {
      setMessage('Enter a username');
      return;
    }
    const dog = dogs.find(d => d.dogId === dogId);
    const confirmMessage = `Are you sure you want to transfer "${dog.registeredName}" to ${username}? This action cannot be undone, and the dog will be permanently transferred to the new owner.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      const res = await fetch(`${API_BASE_URL}/dog/${dogId}/transfer`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ playerId, targetUsername: username })
      });
      if (!res.ok) throw new Error(await res.text());
      setDogs(dogs.filter(d => d.dogId !== dogId));
      setTransferUsername(prev => ({ ...prev, [dogId]: '' }));
      setMessage('Dog transferred successfully');
    } catch (error) {
      setMessage('Transfer failed: ' + error.message);
    }
  };

  const toggleCollapse = (section) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const approvedDogs = dogs.filter(d => 
    d.status === 'Approved' && 
    (!breedFilter || d.breed === breedFilter) && 
    (!kennelFilter || d.kennelId === kennelFilter)
  );
  const pendingDogs = dogs.filter(d => 
    d.status === 'Pending' && 
    (!breedFilter || d.breed === breedFilter) && 
    (!kennelFilter || d.kennelId === kennelFilter)
  );
  const deniedDogs = dogs.filter(d => 
    d.status === 'Denied' && 
    (!breedFilter || d.breed === breedFilter) && 
    (!kennelFilter || d.kennelId === kennelFilter)
  );

  const uniqueBreeds = [...new Set(dogs.map(d => d.breed))];
  const uniqueKennels = [...new Set(dogs.map(d => d.kennelId).filter(id => id))];

  if (!playerId || playerId === '') return (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <p style={{ fontSize: '18px', color: '#333' }}>
        Please login or sign up to register your own dogs with the Dog Fanciers Guild!
      </p>
    </div>
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', padding: '20px' }}>
      <h1 style={{ color: '#1E88E5', marginBottom: '20px', textShadow: '1px 1px 2px rgba(0, 0, 0, 0.1)' }}>
        My Dogs
      </h1>
      <button 
        onClick={() => toggleCollapse('manage')} 
        style={{ 
          padding: '10px 20px', 
          backgroundColor: '#1E88E5', 
          color: 'white', 
          border: 'none', 
          borderRadius: '5px', 
          cursor: 'pointer',
          fontWeight: 'bold',
          marginBottom: '20px',
          transition: 'background-color 0.3s'
        }}
        onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
        onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
      >
        {collapsed.manage ? 'Hide Management' : 'Manage Dogs'}
      </button>
      {message && (
        <div style={{
          backgroundColor: '#BBDEFB',
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '5px',
          color: '#333',
          fontWeight: 'bold',
          textAlign: 'center',
          border: '1px solid #1E88E5'
        }}>
          {message}
        </div>
      )}
      
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center', gap: '20px', backgroundColor: '#E6F0FA', padding: '15px', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)' }}>
        <div>
          <label style={{ marginRight: '10px', fontWeight: 'bold', color: '#333' }}>Filter by Breed:</label>
          <select 
            value={breedFilter} 
            onChange={(e) => setBreedFilter(e.target.value)} 
            style={{ padding: '5px', borderRadius: '5px', border: '1px solid #BBDEFB', backgroundColor: 'white' }}
          >
            <option value="">All Breeds</option>
            {uniqueBreeds.map(breed => (
              <option key={breed} value={breed}>{breed}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ marginRight: '10px', fontWeight: 'bold', color: '#333' }}>Filter by Kennel:</label>
          <select 
            value={kennelFilter} 
            onChange={(e) => setKennelFilter(e.target.value)} 
            style={{ padding: '5px', borderRadius: '5px', border: '1px solid #BBDEFB', backgroundColor: 'white' }}
          >
            <option value="">All Kennels</option>
            {uniqueKennels.map(kennelId => {
              const kennel = dogs.find(d => d.kennelId === kennelId)?.kennelInfo;
              return (
                <option key={kennelId} value={kennelId}>
                  {kennel?.kennelName || 'Unnamed Kennel'}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <h2 
        style={{ 
          cursor: 'pointer', 
          color: collapsed.approved ? '#555' : '#1E88E5', 
          marginBottom: '10px', 
          transition: 'color 0.3s',
          fontSize: '1.5em',
          fontWeight: 'bold'
        }} 
        onClick={() => toggleCollapse('approved')}
      >
        Approved Dogs ({approvedDogs.length}) {collapsed.approved ? '▲' : '▼'}
      </h2>
      {!collapsed.approved && (
        <div style={{ 
          backgroundColor: '#E6F0FA', 
          padding: '20px', 
          borderRadius: '10px', 
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', 
          border: '2px solid #1E88E5',
          marginBottom: '20px'
        }}>
          {approvedDogs.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
              {approvedDogs.map(dog => (
                <div key={dog._id} style={{ 
                  textAlign: 'center', 
                  width: '200px', 
                  padding: '10px', 
                  backgroundColor: '#fff', 
                  borderRadius: '8px', 
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #BBDEFB'
                }}>
                  <Link to={`/dog/${dog.dogId}`}>
                    <img 
                      src={dog.artUrl || 'https://via.placeholder.com/150'} 
                      alt={dog.registeredName} 
                      style={{ maxWidth: '150px', height: '150px', objectFit: 'contain', cursor: 'pointer', borderRadius: '8px' }} 
                    />
                  </Link>
                  <p style={{ margin: '10px 0', fontSize: '16px', color: '#333' }}>
                    {dog.registeredName} <br /> <small>({dog.breed})</small>
                  </p>
                  {collapsed.manage && (
                    <div style={{ marginTop: '10px' }}>
                      <button 
                        onClick={() => markDeceased(dog.dogId)} 
                        style={{ 
                          backgroundColor: '#888', 
                          color: 'white', 
                          padding: '5px 10px', 
                          border: 'none', 
                          borderRadius: '5px', 
                          cursor: 'pointer',
                          marginBottom: '10px',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#666'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#888'}
                      >
                        Mark Deceased
                      </button>
                      <div>
                        <input
                          value={transferUsername[dog.dogId] || ''}
                          onChange={(e) => setTransferUsername({ ...transferUsername, [dog.dogId]: e.target.value })}
                          placeholder="Target Username"
                          style={{ padding: '5px', borderRadius: '5px', border: '1px solid #BBDEFB', marginRight: '10px', width: '120px', backgroundColor: 'white' }}
                        />
                        <button 
                          onClick={() => transferDog(dog.dogId)} 
                          style={{ 
                            padding: '5px 10px', 
                            backgroundColor: '#1E88E5', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '5px', 
                            cursor: 'pointer',
                            transition: 'background-color 0.3s'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
                        >
                          Transfer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#888', fontSize: '16px' }}>No approved dogs yet.</p>
          )}
        </div>
      )}

      <h2 
        style={{ 
          cursor: 'pointer', 
          color: collapsed.pending ? '#555' : '#1E88E5', 
          marginBottom: '10px', 
          transition: 'color 0.3s',
          fontSize: '1.5em',
          fontWeight: 'bold'
        }} 
        onClick={() => toggleCollapse('pending')}
      >
        Pending Dogs ({pendingDogs.length}) {collapsed.pending ? '▲' : '▼'}
      </h2>
      {!collapsed.pending && (
        <div style={{ 
          backgroundColor: '#E6F0FA', 
          padding: '20px', 
          borderRadius: '10px', 
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', 
          border: '2px solid #1E88E5',
          marginBottom: '20px'
        }}>
          {pendingDogs.length > 0 ? (
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              {pendingDogs.map((dog) => (
                <div key={dog._id} style={{ 
                  padding: '15px', 
                  backgroundColor: '#fff', 
                  borderRadius: '8px', 
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)', 
                  marginBottom: '15px',
                  border: '1px solid #BBDEFB'
                }}>
                  <p style={{ fontSize: '16px', color: '#333' }}>
                    {dog.registeredName} ({dog.breed}) - <span style={{ color: '#888' }}>Pending</span>
                  </p>
                  {collapsed.manage && (
                    <div style={{ marginTop: '10px' }}>
                      <input
                        value={transferUsername[dog.dogId] || ''}
                        onChange={(e) => setTransferUsername({ ...transferUsername, [dog.dogId]: e.target.value })}
                        placeholder="Target Username"
                        style={{ padding: '5px', borderRadius: '5px', border: '1px solid #BBDEFB', marginRight: '10px', width: '120px', backgroundColor: 'white' }}
                      />
                      <button 
                        onClick={() => transferDog(dog.dogId)} 
                        style={{ 
                          padding: '5px 10px', 
                          backgroundColor: '#1E88E5', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '5px', 
                          cursor: 'pointer',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
                      >
                        Transfer
                      </button>
                    </div>
                  )}
                  {dog.messages?.length > 0 && (
                    <>
                      <h4 style={{ color: '#1E88E5', margin: '10px 0' }}>Messages</h4>
                      <ul style={{ paddingLeft: '20px', listStyle: 'none' }}>
                        {dog.messages.map((msg, idx) => (
                          <li key={idx} style={{ color: msg.unread && msg.senderRole === 'admin' ? '#EF5350' : '#555', marginBottom: '5px' }}>
                            <strong>{msg.senderRole}:</strong> {msg.text} <small>({new Date(msg.timestamp).toLocaleString()})</small>
                            {msg.unread && msg.senderRole === 'admin' && (
                              <button 
                                onClick={() => markMessageRead(dog._id, idx)} 
                                style={{ 
                                  marginLeft: '10px', 
                                  padding: '4px 10px', 
                                  backgroundColor: '#1E88E5', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '3px', 
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  transition: 'background-color 0.3s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
                              >
                                Mark Read
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                      <textarea
                        value={replies[dog._id] || ''}
                        onChange={(e) => setReplies({ ...replies, [dog._id]: e.target.value })}
                        placeholder="Reply to admin..."
                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #BBDEFB', marginTop: '10px', backgroundColor: 'white' }}
                      />
                      <button 
                        onClick={() => sendReply(dog._id)} 
                        style={{ 
                          marginTop: '10px', 
                          padding: '5px 15px', 
                          backgroundColor: '#1E88E5', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '5px', 
                          cursor: 'pointer',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
                      >
                        Send Reply
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#888', fontSize: '16px' }}>No dogs pending approval.</p>
          )}
        </div>
      )}

      <h2 
        style={{ 
          cursor: 'pointer', 
          color: collapsed.denied ? '#555' : '#1E88E5', 
          marginBottom: '10px', 
          transition: 'color 0.3s',
          fontSize: '1.5em',
          fontWeight: 'bold'
        }} 
        onClick={() => toggleCollapse('denied')}
      >
        Denied Dogs ({deniedDogs.length}) {collapsed.denied ? '▲' : '▼'}
      </h2>
      {!collapsed.denied && (
        <div style={{ 
          backgroundColor: '#E6F0FA', 
          padding: '20px', 
          borderRadius: '10px', 
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', 
          border: '2px solid #1E88E5'
        }}>
          {deniedDogs.length > 0 ? (
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              {deniedDogs.map((dog) => (
                <div key={dog._id} style={{ 
                  padding: '15px', 
                  backgroundColor: '#fff', 
                  borderRadius: '8px', 
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)', 
                  marginBottom: '15px',
                  border: '1px solid #BBDEFB'
                }}>
                  <p style={{ fontSize: '16px', color: '#333' }}>
                    {dog.registeredName} ({dog.breed}) - <span style={{ color: '#EF5350' }}>Denied</span>
                  </p>
                  {dog.denyReason && <p style={{ color: '#888', margin: '5px 0' }}>Reason: {dog.denyReason}</p>}
                  {collapsed.manage && (
                    <div style={{ marginTop: '10px' }}>
                      <input
                        value={transferUsername[dog.dogId] || ''}
                        onChange={(e) => setTransferUsername({ ...transferUsername, [dog.dogId]: e.target.value })}
                        placeholder="Target Username"
                        style={{ padding: '5px', borderRadius: '5px', border: '1px solid #BBDEFB', marginRight: '10px', width: '120px', backgroundColor: 'white' }}
                      />
                      <button 
                        onClick={() => transferDog(dog.dogId)} 
                        style={{ 
                          padding: '5px 10px', 
                          backgroundColor: '#1E88E5', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '5px', 
                          cursor: 'pointer',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
                      >
                        Transfer
                      </button>
                    </div>
                  )}
                  {dog.messages?.length > 0 && (
                    <>
                      <h4 style={{ color: '#1E88E5', margin: '10px 0' }}>Messages</h4>
                      <ul style={{ paddingLeft: '20px', listStyle: 'none' }}>
                        {dog.messages.map((msg, idx) => (
                          <li key={idx} style={{ color: msg.unread && msg.senderRole === 'admin' ? '#EF5350' : '#555', marginBottom: '5px' }}>
                            <strong>{msg.senderRole}:</strong> {msg.text} <small>({new Date(msg.timestamp).toLocaleString()})</small>
                            {msg.unread && msg.senderRole === 'admin' && (
                              <button 
                                onClick={() => markMessageRead(dog._id, idx)} 
                                style={{ 
                                  marginLeft: '10px', 
                                  padding: '4px 10px', 
                                  backgroundColor: '#1E88E5', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '3px', 
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  transition: 'background-color 0.3s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
                              >
                                Mark Read
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <textarea
                    value={replies[dog._id] || ''}
                    onChange={(e) => setReplies({ ...replies, [dog._id]: e.target.value })}
                    placeholder="Reply to admin..."
                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #BBDEFB', marginTop: '10px', backgroundColor: 'white' }}
                  />
                  <div style={{ marginTop: '10px' }}>
                    <button 
                      onClick={() => sendReply(dog._id)} 
                      style={{ 
                        padding: '5px 15px', 
                        backgroundColor: '#1E88E5', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '5px', 
                        cursor: 'pointer',
                        marginRight: '10px',
                        transition: 'background-color 0.3s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
                    >
                      Send Reply
                    </button>
                    <button 
                      onClick={() => clearDeniedDog(dog._id)} 
                      style={{ 
                        padding: '5px 15px', 
                        backgroundColor: '#EF5350', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '5px', 
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#F44336'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#EF5350'}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#888', fontSize: '16px' }}>No denied dogs.</p>
          )}
        </div>
      )}

      <div style={{ marginTop: '30px' }}>
        <Link to={`/in-memoriam/${playerId}`}>
          <button 
            style={{ 
              padding: '10px 20px', 
              backgroundColor: '#1E88E5', 
              color: 'white', 
              border: 'none', 
              borderRadius: '5px', 
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'background-color 0.3s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
          >
            In Memoriam
          </button>
        </Link>
      </div>
    </div>
  );
}

export default MyDogs;